import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styles from './OfferDetails.module.css';
import { fetchClubOfferById, updateClubOffer, deleteClubOffer, fetchOfferCustomers } from '../services/clubService';
import { useCachedPermissions } from '../hooks/useCachedPermissions';
import { showErrorNotification } from '../store/slices/notificationSlice';

const OfferDetails = ({ offer, onBack, onDelete, onSave }) => {
    const dispatch = useDispatch();

    // Club permissions
    const { hasUpdateClub, hasDeleteClub, hasShowCustomerClub, permissionsLoading } = useCachedPermissions();

    const [isEditing, setIsEditing] = useState(false);
    const [editedData, setEditedData] = useState({
        description: '',
        instruction: '',
        from_date: '',
        to_date: '',
        is_active: true,
        image: null,
        imagePreview: null,
    });
    const [apiOffer, setApiOffer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [generalError, setGeneralError] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [customersLoading, setCustomersLoading] = useState(false);
    const [customersError, setCustomersError] = useState(null);

    useEffect(() => {
        let ignore = false;
        async function fetchDetails() {
            setLoading(true);
            setError(null);
            try {
                const data = await fetchClubOfferById(offer.id);
                if (!ignore) {
                    setApiOffer(data);
                    setEditedData({
                        description: data.description || '',
                        instruction: data.instruction || '',
                        from_date: data.from_date || '',
                        to_date: data.to_date || '',
                        is_active: data.is_active,
                        image: null,
                        imagePreview: data.images && data.images.length > 0 ? data.images[0] : null,
                    });
                }
            } catch (err) {
                if (!ignore) setError(err.message || 'Failed to fetch offer details');
            } finally {
                if (!ignore) setLoading(false);
            }
        }
        async function fetchCustomersList() {
            setCustomersLoading(true);
            setCustomersError(null);
            try {
                const data = await fetchOfferCustomers(offer.id);
                if (!ignore) setCustomers(data);
            } catch (err) {
                if (!ignore) setCustomersError(err.message || 'Failed to fetch customers');
            } finally {
                if (!ignore) setCustomersLoading(false);
            }
        }
        fetchDetails();
        fetchCustomersList();
        return () => { ignore = true; };
    }, [offer.id]);

    const handleFieldChange = (field, value) => {
        setEditedData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleImageUpload = () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'image/*';
        fileInput.onchange = (e) => {
            const file = e.target.files[0];
            if (file) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setEditedData(prev => ({
                        ...prev,
                        image: file,
                        imagePreview: reader.result,
                    }));
                };
                reader.readAsDataURL(file);
            }
        };
        fileInput.click();
    };

    const handleSave = async () => {
        setSaving(true);
        setSaveError(null);
        setGeneralError(null);
        try {
            await updateClubOffer(offer.id, {
                description: editedData.description,
                instruction: editedData.instruction,
                from_date: editedData.from_date,
                to_date: editedData.to_date,
                is_active: editedData.is_active,
                image: editedData.image,
            });
            setIsEditing(false);
            // Refresh details after save
            const data = await fetchClubOfferById(offer.id);
            setApiOffer(data);
            if (onSave) {
                onSave(data); // Notify parent to update local data
            }
        } catch (err) {
            setSaveError(err.message || 'Failed to update offer');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setEditedData({
            title: offer.title || '',
            description: offer.description || '',
            date: offer.date || '',
            note: offer.note || '',
            image: offer.image || null
        });
        setIsEditing(false);
    };

    const handleEdit = () => {
        if (!hasUpdateClub) {
            dispatch(showErrorNotification('You do not have permission to edit club offers'));
            return;
        }
        setIsEditing(true);
    };

    const handleDelete = async () => {
        if (!hasDeleteClub) {
            dispatch(showErrorNotification('You do not have permission to delete club offers'));
            return;
        }

        if (!window.confirm('Are you sure you want to delete this offer?')) return;
        setSaving(true);
        setSaveError(null);
        setGeneralError(null);
        try {
            await deleteClubOffer(offer.id);
            if (onDelete) {
                onDelete(offer.id);
            }
        } catch (err) {
            setGeneralError(err.message || 'Failed to delete offer');
        } finally {
            setSaving(false);
        }
    };

    const currentData = isEditing ? editedData : offer;


    return (
        <div className={styles.container}>
            {/* Enhanced Error Messages */}
            {(error || saveError || generalError) && (
                <div className={styles.errorMessage}>
                    {error && (
                        <div><b>Fetch Error:</b> {error}</div>
                    )}
                    {saveError && (
                        <div><b>Save Error:</b> {saveError}</div>
                    )}
                    {generalError && (
                        <div><b>Error:</b> {generalError}</div>
                    )}
                </div>
            )}
            <div className={styles.content}>
                <div className={styles.header}>
                    <div className={styles.backSection}>
                        <button className={styles.backButton} onClick={onBack}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5" />
                                <path d="M12 19l-7-7 7-7" />
                            </svg>
                            Back
                        </button>
                    </div>
                    <h2>Offer Details</h2>
                    <div className={styles.headerActions}>
                        {isEditing ? (
                            <>
                                <button className={styles.cancelButton} onClick={handleCancel}>
                                    Cancel
                                </button>
                                <button className={styles.saveButton} onClick={handleSave} disabled={saving}>
                                    {saving ? 'Saving...' : 'Save'}
                                </button>
                            </>
                        ) : (
                            <>
                                {hasUpdateClub && (
                                    <button className={styles.editButton} onClick={handleEdit}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                            <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                        </svg>
                                        Edit
                                    </button>
                                )}
                                {hasDeleteClub && (
                                    <button className={styles.deleteButton} onClick={handleDelete} disabled={saving}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="3,6 5,6 21,6" />
                                            <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2" />
                                            <line x1="10" y1="11" x2="10" y2="17" />
                                            <line x1="14" y1="11" x2="14" y2="17" />
                                        </svg>
                                        Delete
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className={styles.mainContent}>
                    <div className={styles.leftContent}>
                        <div className={styles.formSection}>
                            {/* Show from_date TO to_date, then description, then instruction */}
                            {!isEditing ? (
                                loading ? (
                                    <div className={styles.formGroup}><span>Loading...</span></div>
                                ) : error ? (
                                    <div className={styles.formGroup}><span>{error}</span></div>
                                ) : apiOffer ? (
                                    <>
                                        <div className={styles.formGroup}>
                                            <label className={styles.label}>Date Range:</label>
                                            <div className={styles.dateRange}>
                                                {apiOffer.from_date && apiOffer.to_date && (
                                                    <span>
                                                        {apiOffer.from_date}{' '}
                                                        <b className={styles.primaryColor}>TO</b>{' '}
                                                        {apiOffer.to_date}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className={styles.formGroup}>
                                            <label className={styles.label}>Description:</label>
                                            <div className={styles.description}>{apiOffer.description}</div>
                                        </div>
                                        <div className={styles.formGroup}>
                                            <label className={styles.label}>Instruction:</label>
                                            <div className={styles.instruction}>{apiOffer.instruction}</div>
                                        </div>
                                        <div className={styles.formGroup}>
                                            <label className={styles.label}>Active Status:</label>
                                            <div className={styles.status}>
                                                {apiOffer.is_active ? (
                                                    <span className={styles.active}>Active</span>
                                                ) : (
                                                    <span className={styles.inactive}>Inactive</span>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                ) : null
                            ) : (
                                <>
                                    <div className={styles.formGroup}>
                                        <label>From Date:</label>
                                        <input
                                            type="date"
                                            value={editedData.from_date ? editedData.from_date.slice(0, 10) : ''}
                                            onChange={e => handleFieldChange('from_date', e.target.value)}
                                            className={styles.input}
                                        />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>To Date:</label>
                                        <input
                                            type="date"
                                            value={editedData.to_date ? editedData.to_date.slice(0, 10) : ''}
                                            onChange={e => handleFieldChange('to_date', e.target.value)}
                                            className={styles.input}
                                        />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Description:</label>
                                        <textarea
                                            value={editedData.description}
                                            onChange={e => handleFieldChange('description', e.target.value)}
                                            className={styles.textarea}
                                            rows="6"
                                        />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Instruction:</label>
                                        <textarea
                                            value={editedData.instruction}
                                            onChange={e => handleFieldChange('instruction', e.target.value)}
                                            className={styles.textarea}
                                            rows="4"
                                        />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Status:</label>
                                        <div className={styles.radioGroup}>
                                            <label className={styles.radioOption}>
                                                <input
                                                    type="radio"
                                                    name="is_active"
                                                    value="true"
                                                    checked={editedData.is_active === true}
                                                    onChange={() => setEditedData(prev => ({ ...prev, is_active: true }))}
                                                />
                                                <span>Active</span>
                                            </label>
                                            <label className={styles.radioOption}>
                                                <input
                                                    type="radio"
                                                    name="is_active"
                                                    value="false"
                                                    checked={editedData.is_active === false}
                                                    onChange={() => setEditedData(prev => ({ ...prev, is_active: false }))}
                                                />
                                                <span>Inactive</span>
                                            </label>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <div className={styles.rightContent}>
                        <div className={styles.imageSection}>
                            <h3>Offer Images</h3>
                            {loading ? (
                                <div className={styles.loading}>Loading images...</div>
                            ) : error ? (
                                <div className={styles.loading}>{error}</div>
                            ) : isEditing ? (
                                <>
                                    {editedData.imagePreview ? (
                                        <div className={styles.imageContainer} style={{ position: 'relative' }}>
                                            <img src={editedData.imagePreview} alt="Preview" />
                                            <button
                                                className={styles.removeButton}
                                                style={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}
                                                onClick={() => setEditedData(prev => ({ ...prev, image: null, imagePreview: null }))}
                                                type="button"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ) : (
                                        <div
                                            className={styles.uploadContainer}
                                            style={{ cursor: 'pointer' }}
                                            tabIndex={0}
                                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleImageUpload(); }}
                                        >
                                            <div className={styles.uploadIcon}>
                                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                    <polyline points="7,10 12,15 17,10" />
                                                    <line x1="12" y1="15" x2="12" y2="3" />
                                                </svg>
                                            </div>
                                            <p className={styles.uploadText}>Upload Offer Image</p>
                                            <button
                                                className={styles.uploadButton}
                                                type="button"
                                                onClick={e => { e.stopPropagation(); handleImageUpload(); }}
                                            >
                                                Upload Image
                                            </button>
                                        </div>
                                    )}
                                </>
                            ) : apiOffer && apiOffer.images && apiOffer.images.length > 0 ? (
                                <div className={styles.imageGallery}>
                                    {apiOffer.images.map((img, idx) => (
                                        <div className={styles.imageContainer} key={idx}>
                                            <img src={img} alt={`Offer ${idx + 1}`} />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className={styles.uploadContainer}>
                                    <div className={styles.uploadIcon}>
                                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                            <polyline points="7,10 12,15 17,10" />
                                            <line x1="12" y1="15" x2="12" y2="3" />
                                        </svg>
                                    </div>
                                    <p className={styles.uploadText}>No images available</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {hasShowCustomerClub && (
                    <div className={styles.customersTableSection}>
                        <h3>Customers Requested This Offer</h3>
                        {customersLoading ? (
                            <div>Loading customers...</div>
                        ) : customersError ? (
                            <div className={styles.errorMessage}>{customersError}</div>
                        ) : customers.length === 0 ? (
                            <div>No customers have requested this offer yet.</div>
                        ) : (
                            <table className={styles.customersTable}>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Phone Number</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {customers.map((customer, idx) => (
                                        <tr key={idx}>
                                            <td>{customer.name}</td>
                                            <td>{customer.email}</td>
                                            <td>{customer.phone}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default OfferDetails; 